import React from "react";
import { Avatar, Badge, Overline } from "../../ForBrands/ui/BrandUI";
import HoverCard, { useHoverCard } from "../../ForBrands/ui/HoverCard";
import { STATUS_COLORS, hoverCardModel, initialsOf } from "./coachUtils";

// Athlete preview (Figma HoverCard 1552:7416): opens below its anchor on
// hover or keyboard focus (a tap on touch focuses the anchor), closes on
// mouse leave, blur or Esc, flips above when there is no room below and
// shifts sideways to stay in the viewport. The card is not interactive, so
// pointer events pass through it. The placement, flip, shift, zoom
// correction and Esc live in the shared ForBrands/ui/HoverCard; this file
// keeps the athlete body.

const Stat = ({ label, value, sub }) => (
  <span className="flex flex-col gap-[3px]">
    <Overline>{label}</Overline>
    <span className="bm-num-m text-bm-ink">{value}</span>
    {sub && <span className="bm-body-s text-bm-tertiary">{sub}</span>}
  </span>
);

const HoverCardBody = ({ player, model }) => (
  <span className="flex w-[288px] flex-col gap-3 overflow-hidden rounded-[12px] border border-bm-border bg-white p-4 text-left shadow-[0_8px_24px_rgba(0,0,0,0.14)]">
    <span className="flex items-center gap-3">
      <Avatar src={player.avatar} initials={initialsOf(player)} size={40} tone="outline" />
      <span className="flex min-w-0 flex-col gap-[2px]">
        <span className="truncate bm-h-s text-bm-ink">{model.name}</span>
        {model.subtitle && <span className="truncate bm-body-s text-bm-muted">{model.subtitle}</span>}
      </span>
    </span>
    <span className="h-px w-full bg-bm-mutedbg" />
    {model.pending ? (
      <span>
        <Badge tone="outline" dot={STATUS_COLORS.pending}>
          Pending activation
        </Badge>
      </span>
    ) : (
      <>
        <span className="flex justify-between gap-4">
          <Stat label="CacheScore" value={model.score} sub={model.scoreSub} />
          <Stat label="CacheValue" value={model.cacheValue} sub={model.valueSub} />
        </span>
        {model.archetype && (
          <span>
            <Badge tone="outline" dot={model.archetype.color}>
              {model.archetype.label}
            </Badge>
          </span>
        )}
      </>
    )}
  </span>
);

// `className` positions the anchor wrapper (relative inline by default; a
// chart dot passes its own absolute placement). While open, the wrapper
// is lifted above its siblings so the card is never covered.
const AthleteHoverCard = ({ player, archetype, teamName, className = "relative inline-flex", style, children }) => {
  const hover = useHoverCard();
  if (!player) return children;

  return (
    <span ref={hover.anchorRef} className={`${className} ${hover.open ? "z-40" : ""}`} style={style} {...hover.anchorProps}>
      {children}
      <HoverCard anchorRef={hover.anchorRef} open={hover.open} onClose={hover.hide} placement="bottom">
        {/* The model is only built while the card is open, as before. */}
        {hover.open && <HoverCardBody player={player} model={hoverCardModel(player, { teamName, archetype })} />}
      </HoverCard>
    </span>
  );
};

export default AthleteHoverCard;
